import { configureStore } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import locales from 'locale/index';
import { loadTranslations, setLocale, syncTranslationWithStore } from 'react-redux-i18n';
import createDebounce from 'redux-debounced';
import { FLUSH, PAUSE, PERSIST, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import promise from 'redux-promise-middleware';
import * as Sentry from '@sentry/react';
import { analyticsMiddleware } from 'store/app/entities/AnalyticsSlice';
import { appDataMiddleware } from 'store/app/entities/AppDataSlice';
import { articlesMiddleware } from 'store/app/entities/ArticlesSlice';
import { cloudMirrorMiddleware } from 'store/app/entities/CloudMirrorSlice';
import { companiesMiddleware } from 'store/app/entities/CompaniesSlice';
import { contentsMiddleware } from 'store/app/entities/ContentsSlice';
import { eSignatureProviderMiddleware } from 'store/app/entities/ESignatureProviderSlice';
import { CANCEL_EMPLOYEE_IMPORT } from 'store/app/entities/employeeImports/action';
import { CREATE_IMPORT } from 'store/app/entities/employees/action';
import { employeesApiMiddleware } from 'store/app/entities/EmployeesApiSlice';
import { groupsMiddleware } from 'store/app/entities/GroupsSlice';
import { messagesApiMiddleware } from 'store/app/entities/MessagesApiSlice';
import { initiativesMiddleware } from 'store/app/entities/InitiativesSlice';
import { sharedFilesMiddleware } from 'store/app/entities/SharedFilesSlice';
import { teamMembershipRulesMiddleware } from 'store/app/entities/TeamMembershipRulesSlice';
import { teamsMiddleware } from 'store/app/entities/TeamsSlice';
import { usersMiddleware } from 'store/app/entities/UsersSlice';
import { wbMiddleware } from 'store/app/entities/WbSlice';
import { ommnioSignMiddleware } from 'store/app/entities/OmmnioSignSlice';
import { batchMiddleware } from 'store/app/entities/BatchSlice';
import { otaMiddleware } from 'store/app/entities/OtaSlice';
import { aiConfigMiddleware } from 'store/app/entities/AiConfigSlice';
import { OPEN_TEAM_DRAWER } from 'store/app/ui/drawers/team/action';
import { SET_FOCUS } from 'store/app/ui/focus/action';
import asyncDispatchMiddleware from 'store/asyncDispatchMiddleware';
import linkPreviewMessagesApiSyncMiddleware from 'store/linkPreviewMessagesApiSyncMiddleware';
import { unauthenticatedMiddleware } from 'store/unauthenticatedMiddleware';
import impersonationMiddleware from 'store/impersonationMiddleware';
import preferredLocale from 'utils/preferredLocale';
import authActions from 'store/app/auth/action';
import { registerImpersonationExpiredHandler, registerStore } from 'utils/authStateRegistry';
import { firebaseBeaconMiddleware } from 'utils/reduxBeaconConfig';
import { sentryActionTransformer } from 'store/sentryRedux';

import createRootReducer from './app/reducer';

export const history = createBrowserHistory();

// Create Sentry Redux enhancer to capture Redux state in error reports
// Only include minimal state to avoid sending huge payloads
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Transform state to only include relevant, non-sensitive information
  stateTransformer: state => {
    if (!state) return state;
    // Only include minimal state slices that are useful for debugging
    return {
      // Include routing state as it's small and useful for context
      router: state.router,
      // Include locale as it's small and useful
      i18n: state.i18n
        ? {
            locale: state.i18n.locale,
          }
        : undefined,
      // Include minimal auth state (user ID only, not full user objects)
      auth: state.auth
        ? {
            user: state.auth.user ? { id: state.auth.user.id } : undefined,
          }
        : undefined,
      // Don't include entity caches (messages, channels, employees, etc.) as they're huge
    };
  },
  actionTransformer: sentryActionTransformer,
});

const store = configureStore({
  reducer: createRootReducer(),
  enhancers: getDefaultEnhancers => getDefaultEnhancers().concat(sentryReduxEnhancer),
  middleware: getDefaultMiddleware => {
    const middlewareArray = getDefaultMiddleware({
      immutableCheck: {
        ignoredPaths: ['drawers.employeeImports.file'],
      },
      serializableCheck: {
        ignoredPaths: ['firebaseBeaconReducer', 'drawers.employeeImports.file'],
        ignoredActionPaths: [
          'payload',
          'meta.baseQueryMeta.request',
          'meta.baseQueryMeta.response',
          'meta.arg.originalArgs.pdf', // Allow Blob for OmmnioSign PDF uploads
        ],
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          CREATE_IMPORT,
          `${CREATE_IMPORT}_PENDING`,
          `${CREATE_IMPORT}_FULFILLED`,
          `${CREATE_IMPORT}_REJECTED`,
          CANCEL_EMPLOYEE_IMPORT,
          `${CANCEL_EMPLOYEE_IMPORT}_PENDING`,
          `${CANCEL_EMPLOYEE_IMPORT}_FULFILLED`,
          `${CANCEL_EMPLOYEE_IMPORT}_REJECTED`,
          SET_FOCUS,
          OPEN_TEAM_DRAWER,
        ],
      },
    }).concat(
      unauthenticatedMiddleware,
      impersonationMiddleware,
      promise,
      linkPreviewMessagesApiSyncMiddleware,
      createDebounce(),
      analyticsMiddleware,
      appDataMiddleware,
      articlesMiddleware,
      cloudMirrorMiddleware,
      companiesMiddleware,
      contentsMiddleware,
      eSignatureProviderMiddleware,
      employeesApiMiddleware,
      firebaseBeaconMiddleware,
      groupsMiddleware,
      messagesApiMiddleware,
      initiativesMiddleware,
      sharedFilesMiddleware,
      teamMembershipRulesMiddleware,
      teamsMiddleware,
      usersMiddleware,
      wbMiddleware,
      ommnioSignMiddleware,
      batchMiddleware,
      otaMiddleware,
      aiConfigMiddleware,
      asyncDispatchMiddleware,
    );
    return middlewareArray;
  },
});

export const persistor = persistStore(store);

registerStore(store);

registerImpersonationExpiredHandler(() => {
  store.dispatch(authActions.exitImpersonation({ reason: 'expired' }));
});

syncTranslationWithStore(store);

store.dispatch(loadTranslations({ ...locales }));
store.dispatch(setLocale(preferredLocale()));

export default store;
